import { css, keyframes } from '@emotion/react';
import {
  Accordion,
  type AccordionProps,
  Alert,
  Anchor,
  AppShell,
  Avatar,
  Badge,
  type BadgeProps,
  Breadcrumbs,
  Button,
  Card,
  type CardProps,
  Checkbox,
  Chip,
  type ChipProps,
  Code,
  Combobox,
  createTheme,
  Drawer,
  getRadius,
  Input,
  Loader,
  LoadingOverlay,
  type MantineLoaderComponent,
  type MantineSpacing,
  type MantineTheme,
  Menu,
  Modal,
  NavLink,
  Notification,
  type NotificationProps,
  Pagination,
  Paper,
  type PaperProps,
  Popover,
  Progress,
  rem,
  SegmentedControl,
  type SegmentedControlProps,
  Select,
  Stepper,
  Switch,
  Table,
  Tabs,
  Text,
  Timeline,
  Tooltip,
} from '@mantine/core';
import '@mantine/core/styles.css';
import { DatePickerInput } from '@mantine/dates';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';
import { FiveBars } from '../components/compatibility/LoaderFiveBars';

import './fonts.css';
import { Dropzone } from '@mantine/dropzone';

interface ExtendedNotificationProps extends NotificationProps {
  semanticVariant?: 'default' | 'success' | 'error' | 'warning';
  grayBorder?: boolean;
}

const defaultLoaders = (
  Loader as unknown as {
    defaultLoaders: Record<string, MantineLoaderComponent>;
  }
).defaultLoaders;

// Typography scale - Major Third (1:250) with 16px base
const TYPOGRAPHY_SCALE = {
  xxs: '10px', //0.625rem
  xs: '12px', // 0.75rem
  sm: '14px', // 0.875rem
  base: '16px', // 1rem
  lg: '18px', // 1.125rem
  xl: '20px', // 1.25rem
  '2xl': '24px', // 1.5rem
  '3xl': '32px', // 2rem
  '4xl': '40px', // 2.5rem
  '5xl': '48px', // 3rem
  '6xl': '64px', // 4rem
} as const;

const SPACING_SCALE = {
  none: '0px', // spacing-none
  xxs: '2px', // spacing-xxs
  xs: '4px', // spacing-xs
  sm: '8px', // spacing-sm
  md: '12px', // spacing-md
  lg: '16px', // spacing-lg
  xl: '24px', // spacing-xl
  xxl: '32px', // spacing-xxl
} as const;

const RADIUS_SCALE = {
  none: '0px', // radius-none
  xs: '2px', // radius-xs
  sm: '4px', // radius-sm
  md: '8px', // radius-md
  lg: '16px', // radius-lg
  xl: '24px', // radius-xl
  xxl: '32px', // radius-xxl
} as const;

type BadgeColorKey = 'brand' | 'success' | 'warning' | 'error' | 'blue' | 'gray' | 'cyan' | 'other';

/**
 * Badge surfaces from ui-template package chips (`light-dark` + `--mantine-color-*`).
 * Label colour is one scale step toward the surface (7 on light, 2 on dark) for softer contrast.
 */
const UI_TEMPLATE_BADGE_SEMANTIC = {
  brand: {
    background:
      'light-dark(color-mix(in srgb, var(--mantine-color-brand-1) 72%, var(--mantine-color-white)), color-mix(in srgb, var(--mantine-color-brand-9) 45%, transparent))',
    color: 'light-dark(var(--mantine-color-brand-7), var(--mantine-color-brand-2))',
    borderColor: 'light-dark(var(--mantine-color-brand-3), var(--mantine-color-brand-7))',
  },
  success: {
    background:
      'light-dark(color-mix(in srgb, var(--mantine-color-success-1) 76%, var(--mantine-color-white)), color-mix(in srgb, var(--mantine-color-success-9) 42%, transparent))',
    color: 'light-dark(var(--mantine-color-success-7), var(--mantine-color-success-2))',
    borderColor: 'light-dark(var(--mantine-color-success-3), var(--mantine-color-success-7))',
  },
  blue: {
    background:
      'light-dark(color-mix(in srgb, var(--mantine-color-blue-1) 78%, var(--mantine-color-white)), color-mix(in srgb, var(--mantine-color-blue-9) 40%, transparent))',
    color: 'light-dark(var(--mantine-color-blue-7), var(--mantine-color-blue-2))',
    borderColor: 'light-dark(var(--mantine-color-blue-3), var(--mantine-color-blue-7))',
  },
  warning: {
    background:
      'light-dark(color-mix(in srgb, var(--mantine-color-warning-1) 78%, var(--mantine-color-white)), color-mix(in srgb, var(--mantine-color-warning-9) 40%, transparent))',
    color: 'light-dark(var(--mantine-color-warning-7), var(--mantine-color-warning-2))',
    borderColor: 'light-dark(var(--mantine-color-warning-3), var(--mantine-color-warning-7))',
  },
  error: {
    background:
      'light-dark(color-mix(in srgb, var(--mantine-color-error-1) 78%, var(--mantine-color-white)), color-mix(in srgb, var(--mantine-color-error-9) 40%, transparent))',
    color: 'light-dark(var(--mantine-color-error-7), var(--mantine-color-error-2))',
    borderColor: 'light-dark(var(--mantine-color-error-3), var(--mantine-color-error-7))',
  },
  cyan: {
    background:
      'light-dark(color-mix(in srgb, var(--mantine-color-cyan-1) 78%, var(--mantine-color-white)), color-mix(in srgb, var(--mantine-color-cyan-9) 40%, transparent))',
    color: 'light-dark(var(--mantine-color-cyan-7), var(--mantine-color-cyan-2))',
    borderColor: 'light-dark(var(--mantine-color-cyan-3), var(--mantine-color-cyan-7))',
  },
  gray: {
    background:
      'light-dark(color-mix(in srgb, var(--mantine-color-gray-0) 86%, var(--mantine-color-white)), color-mix(in srgb, var(--mantine-color-dark-7) 60%, transparent))',
    color: 'light-dark(var(--mantine-color-gray-8), var(--mantine-color-gray-dark-1))',
    borderColor:
      'light-dark(var(--mantine-color-gray-3), color-mix(in srgb, var(--mantine-color-dark-3) 75%, transparent))',
  },
} as const;

const UI_TEMPLATE_BADGE_FILLED_NEUTRAL = {
  border: '1px solid light-dark(var(--mantine-color-gray-4), var(--mantine-color-gray-dark-7))',
  backgroundColor: 'light-dark(var(--mantine-color-gray-2), var(--mantine-color-gray-dark-9))',
  color: 'light-dark(var(--mantine-color-gray-7), var(--mantine-color-gray-dark-3))',
} as const;

/** First segment of Mantine colour keys like `success.7` for badge theme resolution. */
function badgeColorBaseKey(color: BadgeProps['color']): BadgeColorKey {
  if (color == null || color === '') {
    return 'brand';
  }

  const raw = String(color);
  const base = raw.includes('.') ? raw.slice(0, raw.indexOf('.')) : raw;

  if (base === 'success' || base === 'green' || base === 'teal') {
    return 'success';
  }
  if (base === 'warning' || base === 'orange' || base === 'yellow') {
    return 'warning';
  }
  if (base === 'error' || base === 'red') {
    return 'error';
  }
  if (base === 'cyan') {
    return 'cyan';
  }
  if (base === 'blue' || base === 'indigo') {
    return 'blue';
  }
  if (base === 'gray' || base === 'grey') {
    return 'gray';
  }
  if (base === 'brand' || base === 'violet' || base === 'grape') {
    return 'brand';
  }

  return 'other';
}

/** Neutral “chip” filled style only when no semantic colour is chosen (matches app-template usage). */
function shouldUseNeutralFilledBadge(color: BadgeProps['color']): boolean {
  if (color == null || color === '') {
    return true;
  }
  return badgeColorBaseKey(color) === 'gray';
}

function getUiTemplateSemanticBadgeSurface(color: BadgeProps['color']) {
  switch (badgeColorBaseKey(color)) {
    case 'success':
      return { ...UI_TEMPLATE_BADGE_SEMANTIC.success };
    case 'warning':
      return { ...UI_TEMPLATE_BADGE_SEMANTIC.warning };
    case 'error':
      return { ...UI_TEMPLATE_BADGE_SEMANTIC.error };
    case 'blue':
      return { ...UI_TEMPLATE_BADGE_SEMANTIC.blue };
    case 'cyan':
      return { ...UI_TEMPLATE_BADGE_SEMANTIC.cyan };
    case 'gray':
      return { ...UI_TEMPLATE_BADGE_SEMANTIC.gray };
    default:
      return { ...UI_TEMPLATE_BADGE_SEMANTIC.brand };
  }
}

export const theme = createTheme({
  white: '#FFFFFF',
  black: '#000000',
  // Set Montserrat as the default font family
  fontFamily: 'Montserrat',
  // Define font sizes including xxs for Text size prop
  fontSizes: {
    xxs: TYPOGRAPHY_SCALE.xxs, // 10px
    xs: TYPOGRAPHY_SCALE.xs, // 12px
    sm: TYPOGRAPHY_SCALE.sm, // 14px
    md: TYPOGRAPHY_SCALE.base, // 16px
    lg: TYPOGRAPHY_SCALE.lg, // 18px
    xl: TYPOGRAPHY_SCALE.xl, // 20px
  },
  colors: {
    // Gray scale (light mode optimized)
    gray: [
      '#FDFDFD', // 0 - 25
      '#FAFAFA', // 1 - 50
      '#F5F5F5', // 2 - 100
      '#E9EAEB', // 3 - 200
      '#D5D7DA', // 4 - 300
      '#A4A7AE', // 5 - 400
      '#717680', // 6 - 500
      '#535862', // 7 - 600
      '#414651', // 8 - 700
      '#252B37', // 9 - 800
      '#181D27', // 10 - 900
      '#0A0D12', // 11 - 950
    ],
    // Gray scale (dark mode optimized)
    'gray-dark': [
      '#FAFAFA', // 0 - 25
      '#F7F7F7', // 1 - 50
      '#F0F0F1', // 2 - 100
      '#ECECED', // 3 - 200
      '#CECFD2', // 4 - 300
      '#94979C', // 5 - 400
      '#85888E', // 6 - 500
      '#61656C', // 7 - 600
      '#373A41', // 8 - 700
      '#22262F', // 9 - 800
      '#13161B', // 10 - 900
      '#0C0E12', // 11 - 950
    ],
    // Brand colors - primary purple palette
    brand: [
      '#F9F5FF', // 0 - very light for backgrounds
      '#F4EBFF', // 1 - light for subtle backgrounds
      '#E9D7FE', // 2 - lighter for hover states
      '#D6BBFB', // 3 - light medium
      '#B692F6', // 4 - medium light
      '#9E77ED', // 5 - medium
      '#7F56D9', // 6 - 25 (AA 4.95)
      '#6941C6', // 7 - 50 (AA 6.61)
      '#5925DC', // 8 - slightly darker
      '#5025B8', // 9 - 100 (AAA 9.14)
    ],
    // Error colors - red palette for destructive actions
    error: [
      '#FEF3F2', // 50 - #FEF3F2
      '#FEE4E2', // 100 - #FEE4E2
      '#FECDCA', // 200 - #FECDCA
      '#FDA29B', // 300 - #FDA29B
      '#F97066', // 400 - #F97066
      '#F04438', // 500 - #F04438
      '#D92D20', // 600 - #D92D20
      '#B42318', // 700 - #B42318
      '#912018', // 800 - #912018
      '#7A271A', // 900 - #7A271A
    ],
    // Warning colors - yellow/orange palette for caution
    warning: [
      '#FFFAEB', // 50 - #FFFAEB
      '#FEF0C7', // 100 - #FEF0C7
      '#FEDF89', // 200 - #FEDF89
      '#FEC84B', // 300 - #FEC84B
      '#FDB022', // 400 - #FDB022
      '#F79009', // 500 - #F79009
      '#DC6803', // 600 - #DC6803
      '#B54708', // 700 - #B54708
      '#93370D', // 800 - #93370D
      '#7A2E0E', // 900 - #7A2E0E
    ],
    // Success colors - green palette for positive actions
    success: [
      '#ECFDF3', // 50 - #ECFDF3
      '#D1FAE5', // 100 - #D1FAE5
      '#A7F3D0', // 200 - #A7F3D0
      '#6EE7B7', // 300 - #6EE7B7
      '#34D399', // 400 - #34D399
      '#10B981', // 500 - #10B981
      '#059669', // 600 - #059669
      '#047857', // 700 - #047857
      '#065F46', // 800 - #065F46
      '#064E3B', // 900 - #064E3B
    ],
  },
  // Set brand as primary color
  primaryColor: 'brand',
  primaryShade: 9,
  headings: {
    fontFamily: 'Titillium Web',
    fontWeight: '700',
    textWrap: 'pretty',
    sizes: {
      h1: {
        fontSize: TYPOGRAPHY_SCALE['4xl'], // 40px
        lineHeight: '48px', // 1.2 ratio
      },
      h2: {
        fontSize: TYPOGRAPHY_SCALE['3xl'], // 32px
        lineHeight: '40px', // 1.35 ratio
      },
      h3: {
        fontSize: TYPOGRAPHY_SCALE['2xl'], // 24px
        lineHeight: '32px', // 1.33 ratio
      },
      h4: {
        fontSize: TYPOGRAPHY_SCALE.lg, // 18px
        lineHeight: '24px', // 1.2 ratio
      },
      h5: {
        fontSize: TYPOGRAPHY_SCALE.base, // 16px
        lineHeight: '20px', // 1.25 ratio
      },
      h6: {
        fontSize: TYPOGRAPHY_SCALE.xs, // 12px
        lineHeight: '16px', // 1.33 ratio
      },
    },
  },
  spacing: SPACING_SCALE,
  radius: RADIUS_SCALE,
  shadows: {
    xs: '0 1px 2px 0 rgba(0, 0, 0, 0.10), 0 1px 3px 0 rgba(0, 0, 0, 0.05)',
    sm: '0 7px 7px -5px rgba(0, 0, 0, 0.04), 0 10px 15px -5px rgba(0, 0, 0, 0.10), 0 1px 3px 0 rgba(0, 0, 0, 0.05)',
    md: '0 10px 10px -5px rgba(0, 0, 0, 0.04), 0 20px 25px -5px rgba(0, 0, 0, 0.10), 0 1px 3px 0 rgba(0, 0, 0, 0.05)',
    lg: '0 12px 12px -7px rgba(0, 0, 0, 0.04), 0 28px 23px -7px rgba(0, 0, 0, 0.10), 0 1px 3px 0 rgba(0, 0, 0, 0.05)',
    xl: '0 17px 17px -7px rgba(0, 0, 0, 0.04), 0 36px 28px -7px rgba(0, 0, 0, 0.10), 0 1px 3px 0 rgba(0, 0, 0, 0.05)',
  } as const,
  components: {
    Anchor: Anchor.extend({
      styles: () => ({
        root: {
          fontSize: 'inherit',
          lineHeight: 'inherit',
        },
      }),
    }),
    Text: Text.extend({
      defaultProps: {
        variant: 'bodySmall',
        color: 'light-dark(var(--mantine-color-gray-9), var(--mantine-color-gray-dark-2))',
      },
      styles: (theme: MantineTheme, props: { variant?: string }) => {
        if (props.variant === 'h1') {
          return {
            root: {
              fontSize: TYPOGRAPHY_SCALE['4xl'], // 40px
              fontFamily: theme.headings?.fontFamily,
              lineHeight: theme.headings?.sizes?.h1?.lineHeight,
              fontWeight: theme.headings?.fontWeight,
            },
          };
        }
        if (props.variant === 'h2') {
          return {
            root: {
              fontSize: TYPOGRAPHY_SCALE['3xl'], // 32px
              fontFamily: theme.headings?.fontFamily,
              lineHeight: theme.headings?.sizes?.h2?.lineHeight,
              fontWeight: theme.headings?.fontWeight,
            },
          };
        }
        if (props.variant === 'h3') {
          return {
            root: {
              fontSize: TYPOGRAPHY_SCALE['2xl'], // 24px
              fontFamily: theme.headings?.fontFamily,
              lineHeight: theme.headings?.sizes?.h3?.lineHeight,
              fontWeight: theme.headings?.fontWeight,
            },
          };
        }
        if (props.variant === 'h4') {
          return {
            root: {
              fontSize: TYPOGRAPHY_SCALE.lg, // 18px
              fontFamily: theme.headings?.fontFamily,
              lineHeight: theme.headings?.sizes?.h4?.lineHeight,
              fontWeight: theme.headings?.fontWeight,
            },
          };
        }
        if (props.variant === 'h5') {
          return {
            root: {
              fontSize: TYPOGRAPHY_SCALE.base, // 16px
              fontFamily: theme.headings?.fontFamily,
              lineHeight: theme.headings?.sizes?.h5?.lineHeight,
              fontWeight: theme.headings?.fontWeight,
            },
          };
        }
        if (props.variant === 'h6') {
          return {
            root: {
              fontSize: TYPOGRAPHY_SCALE.xs, // 12px
              fontFamily: theme.headings?.fontFamily,
              lineHeight: theme.headings?.sizes?.h6?.lineHeight,
              fontWeight: theme.headings?.fontWeight,
            },
          };
        }
        if (props.variant === 'bodyLarge') {
          return {
            root: {
              fontSize: TYPOGRAPHY_SCALE.base,
              lineHeight: '1.5rem',
              fontWeight: 500,
            },
          };
        }
        if (props.variant === 'body') {
          return {
            root: {
              fontSize: TYPOGRAPHY_SCALE.sm,
              lineHeight: '1.25rem',
              fontWeight: 500,
            },
          };
        }
        if (props.variant === 'bodySmall') {
          return {
            root: {
              fontSize: TYPOGRAPHY_SCALE.xs,
              lineHeight: '1rem',
              fontWeight: 500,
            },
          };
        }
        if (props.variant === 'bodyLargeBold') {
          return {
            root: {
              fontSize: TYPOGRAPHY_SCALE.base,
              lineHeight: '1.5rem',
              fontWeight: 700,
            },
          };
        }
        if (props.variant === 'bodyBold') {
          return {
            root: {
              fontSize: TYPOGRAPHY_SCALE.sm,
              lineHeight: '1.25rem',
              fontWeight: 700,
            },
          };
        }
        if (props.variant === 'bodySmallBold') {
          return {
            root: {
              fontSize: TYPOGRAPHY_SCALE.xs,
              lineHeight: '1rem',
              fontWeight: 700,
            },
          };
        }
        if (props.variant === 'bodyExtraSmallMediumItalic') {
          return {
            root: {
              fontSize: TYPOGRAPHY_SCALE.xxs,
              lineHeight: '1rem',
              fontWeight: 500,
              fontStyle: 'italic',
            },
          };
        }
        if (props.variant === 'button') {
          return {
            root: {
              fontSize: TYPOGRAPHY_SCALE.sm,
              fontFamily: theme.headings?.fontFamily,
              lineHeight: '1rem',
              fontWeight: 700,
            },
          };
        }
        if (props.variant === 'buttonSmall') {
          return {
            root: {
              fontSize: TYPOGRAPHY_SCALE.xs,
              fontFamily: theme.headings?.fontFamily,
              lineHeight: '1rem',
              fontWeight: 700,
            },
          };
        }
        if (props.variant === 'monospaceSmall') {
          return {
            root: {
              fontSize: TYPOGRAPHY_SCALE.xs,
              fontFamily: theme.fontFamilyMonospace,
              lineHeight: '1.25rem',
            },
          };
        }
        if (props.variant === 'monospaceExtraSmall') {
          return {
            root: {
              fontSize: TYPOGRAPHY_SCALE.xxs,
              fontFamily: theme.fontFamilyMonospace,
              lineHeight: '1rem',
              fontWeight: 700,
            },
          };
        }
        if (props.variant === 'monospaceBold') {
          return {
            root: {
              fontSize: TYPOGRAPHY_SCALE.base,
              fontFamily: theme.fontFamilyMonospace,
              lineHeight: '1.5rem',
              fontWeight: 700,
            },
          };
        }
        if (props.variant === 'monospaceSmallBold') {
          return {
            root: {
              fontSize: TYPOGRAPHY_SCALE.xs,
              fontFamily: theme.fontFamilyMonospace,
              lineHeight: '1.25rem',
              fontWeight: 700,
            },
          };
        }
        if (props.variant === 'label') {
          return {
            root: {
              fontSize: TYPOGRAPHY_SCALE.xs,
              fontFamily: theme.fontFamily,
              lineHeight: '1rem',
              letterSpacing: '0.72px',
            },
          };
        }
        if (props.variant === 'labelAllCaps') {
          return {
            root: {
              fontSize: TYPOGRAPHY_SCALE.xs,
              fontFamily: theme.headings?.fontFamily,
              fontWeight: theme.headings?.fontWeight,
              lineHeight: '1rem',
              textTransform: 'uppercase',
              letterSpacing: '1.2px',
            },
          };
        }
        if (props.variant === 'default') {
          return {
            root: {
              // No fontSize defined - allows size prop to work
              fontFamily: theme.fontFamily,
              lineHeight: '1.5',
              fontWeight: 400,
            },
          };
        }

        return {};
      },
    }),
    NavLink: NavLink.extend({
      styles: (theme: MantineTheme) => ({
        root: {
          color: `${theme.colors?.['gray-dark']?.[3]} !important`, // Always light color for dark gradient background
          fontFamily: theme.fontFamily, // Montserrat font family
          borderRadius: theme.radius?.md,
          transition: 'background-color 150ms ease, color 150ms ease',

          // Ensure proper vertical alignment of label and icon
          '& .mantine-NavLink-body': {
            display: 'flex',
            alignItems: 'center',
          },

          // Ensure all text inside nav items is light colored, but preserve icon fonts
          '& *': {
            color: 'inherit !important',
          },

          // Apply Montserrat only to text elements, not icons
          '& .mantine-Text-root, & span:not(.material-symbols-sharp):not([class*="icon"]), & p': {
            fontFamily: 'inherit !important',
          },

          // Ensure Material Symbols icons keep their font family
          '& .material-symbols-sharp, & [class*="icon"]': {
            fontFamily: '"Material Symbols", sans-serif !important',
          },

          '&:hover': {
            backgroundColor: `${theme.colors?.['gray-dark']?.[8]} !important`, // Gray background for hover
            color: `${theme.colors?.['gray-dark']?.[0]} !important`, // Lighter gray text on hover
          },

          '&[data-active="true"]': {
            backgroundColor: `${theme.colors?.['gray-dark']?.[8]} !important`, // Lighter gray background for active
            color: `${theme.colors?.brand?.[3]} !important`, // Strongest brand color for active state

            // Ensure all text inside active state has bold font weight and strongest brand color
            '& .mantine-Text-root, & span:not(.material-symbols-sharp):not([class*="icon"]), & p': {
              fontWeight: '500 !important',
              color: `${theme.colors?.brand?.[3]} !important`,
            },

            // Icons also use strongest brand color when active
            '& .material-symbols-sharp, & [class*="icon"]': {
              color: `${theme.colors?.brand?.[3]} !important`,
            },
          },
        },
        label: {
          color: 'inherit !important',
          fontFamily: 'inherit !important',
          lineHeight: 'normal !important',
        },
        section: {
          color: 'inherit !important',
          // Don't override font family for the section (where icons are placed)
        },
      }),
    }),
    AppShell: AppShell.extend({
      defaultProps: {
        transitionDuration: 300,
        transitionTimingFunction: 'ease-in-out',
        layout: 'alt',
      },
      styles: (theme: MantineTheme) => ({
        root: {
          '--app-shell-transition-duration': '0ms',
          '--app-shell-transition-timing': 'ease-in-out',
        },
        header: {
          backgroundColor: `light-dark(${theme.white}, ${theme.black})`,
          borderColor: `light-dark(${theme.colors?.gray?.[2]}, ${theme.black})`,
          borderWidth: '1px',
          borderStyle: 'solid',
          borderTop: 'none',
          borderLeft: 'none',
          borderRight: 'none',
          boxShadow: theme.shadows?.sm,
          transition:
            'background-color var(--app-shell-transition-duration) var(--app-shell-transition-timing), box-shadow var(--app-shell-transition-duration) var(--app-shell-transition-timing)',
          zIndex: 100,

          // Mobile gradient background
          '@media (max-width: 768px)': {
            background: 'linear-gradient(93.55deg, #0C0E12 25.69%, #4F24B4 100%)',
            borderBottom: 'none', // Remove border on mobile
          },

          // Header content styling
          '& .mantine-AppShell-header': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: `0 ${theme.spacing?.xl}`,
            height: '100%',
          },

          // Burger menu styling - always white
          '& .mantine-Burger-root': {
            color: `${theme.white} !important`,

            '& .mantine-Burger-burger': {
              backgroundColor: `${theme.white} !important`,

              '&::before, &::after': {
                backgroundColor: `${theme.white} !important`,
              },

              // When opened (close icon), middle line matches header background
              '&[data-opened="true"]': {
                backgroundColor: `transparent !important`,
              },
            },
          },
        },
        navbar: {
          background: 'linear-gradient(177.01deg, #5025B8 -0.73%, #080809 21.65%)',
          borderColor: 'transparent',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderTop: 'none',
          borderBottom: 'none',
          borderLeft: 'none',
          boxShadow: theme.shadows?.sm,
          transition:
            'width var(--app-shell-transition-duration) var(--app-shell-transition-timing), transform var(--app-shell-transition-duration) var(--app-shell-transition-timing), opacity var(--app-shell-transition-duration) var(--app-shell-transition-timing)',

          // Logo and title styling
          '& .logo-title': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: theme.spacing?.md,
            color: theme.colors?.['gray-dark']?.[3],
          },

          // Navbar section headers
          '& .navbar-section-header': {
            color: `${theme.colors?.['gray-dark']?.[4]} !important`, // Always light color for section headers
          },

          // Collapsed state styling - center NavLinks when navbar is collapsed
          '&[data-collapsed="true"]': {
            '& .mantine-NavLink-root': {
              justifyContent: 'center !important',
              padding: `${theme.spacing?.sm} !important`,

              // Center the NavLink body (contains icon and label)
              '& .mantine-NavLink-body': {
                justifyContent: 'center !important',
                width: '100%',
              },

              // Center the left section (icon container) and remove margin
              '& .mantine-NavLink-section[data-position="left"]': {
                marginRight: '0 !important',
                justifyContent: 'center !important',
              },
            },

            // Center the collapse toggle and other flex items
            '& .nav-item': {
              justifyContent: 'center !important',
            },
          },

          // Mobile responsive behavior
          // Use 47.9em (767px) to avoid conflict with Mantine's sm breakpoint at 48em (768px)
          '@media (max-width: 47.9em)': {
            position: 'fixed',
            top: 'var(--app-shell-header-height, 0px)',
            left: 0,
            width: '75%',
            height: 'calc(100vh - var(--app-shell-header-height, 0px))',
            zIndex: 999,
            transform: 'translateX(-100%)',

            '&[data-opened="true"]': {
              transform: 'translateX(0)',
              opacity: 1,
              background: theme.black, // All black background when opened on mobile
            },
          },
        },
        main: {
          backgroundColor: `light-dark(${theme.colors?.['gray-dark']?.[0]}, ${theme.colors?.['gray-dark']?.[11]})`,
          height:
            'calc(100vh - var(--app-shell-header-height, 0px) - var(--app-shell-footer-height, 0px))',
          boxSizing: 'border-box',
          overflow: 'auto',
          transition:
            'margin var(--app-shell-transition-duration) var(--app-shell-transition-timing)',
        },
      }),
    }),
    Switch: Switch.extend({
      defaultProps: {
        withThumbIndicator: false,
      },
    }),
    Chip: Chip.extend({
      defaultProps: { size: 'sm', radius: 4 },
      // Hard-lock the visible pill to 4px regardless of size/variant/prop
      styles: (_theme: MantineTheme, _params: ChipProps) => ({
        label: {
          borderRadius: rem(4), // 4px
        },
      }),
    }),
    Checkbox: Checkbox.extend({
      defaultProps: {
        radius: 'xs',
        size: 'sm',
      },
    }),
    SegmentedControl: SegmentedControl.extend({
      defaultProps: {
        radius: 'sm',
        size: 'sm',
      },
      styles: (theme: MantineTheme, _params: SegmentedControlProps) => {
        return {
          root: {
            backgroundColor: `light-dark(${theme.colors.gray[3]}, ${theme.colors['gray-dark'][8]})`,
          },
          label: {
            color: `light-dark(${theme.black}, ${theme.white})`,
            fontWeight: 400,
          },
          indicator: {
            background: `light-dark(${theme.white}, ${theme.black})`,
          },
        };
      },
    }),
    Button: Button.extend({
      defaultProps: {
        radius: 'sm',
        size: 'sm',
        variant: 'primary',
      },
      styles: (theme: MantineTheme, props: { variant?: string }) => {
        const shared = {
          // Prefer overflow visible to avoid clipping labels (v8 defaults to hidden)
          overflow: 'visible',
          fontWeight: 400,
          '&[data-disabled]': {
            opacity: 1,
            border: 'unset',
            boxShadow: 'none',
            cursor: 'not-allowed',
            backgroundColor: `light-dark(${theme.colors.gray[3]}, ${theme.colors['gray-dark'][8]})`,
            color: `light-dark(${theme.colors.gray[8]}, ${theme.colors['gray-dark'][2]})`,

            '&:hover': {
              border: `unset`,
              backgroundColor: `light-dark(${theme.colors.gray[3]}, ${theme.colors['gray-dark'][8]})`,
              color: `light-dark(${theme.colors.gray[8]}, ${theme.colors['gray-dark'][2]})`,
            },
          },
        } as const;

        const sectionStyles = {
          section: {
            '&[data-position="left"]': {
              marginInlineEnd: theme.spacing.sm,
            },
            '&[data-position="right"]': {
              marginInlineStart: theme.spacing.sm,
            },
          },
        };

        if (props.variant === 'primary') {
          return {
            root: {
              ...shared,

              backgroundColor: theme.colors.brand[9],
              color: theme.white,
              border: `${rem(1)} solid ${theme.colors.brand[9]}`,
              '&:hover': {
                backgroundColor: theme.colors.brand[7],
                border: `${rem(1)} solid ${theme.colors.brand[7]}`,
              },
            },
            ...sectionStyles,
          };
        }
        if (props.variant === 'secondary') {
          return {
            root: {
              ...shared,
              backgroundColor: `light-dark(${theme.white}, ${theme.colors['gray-dark'][10]})`,
              color: `light-dark(${theme.colors.gray[8]}, ${theme.white})`,
              border: `1px solid light-dark(${theme.colors.gray[4]}, ${theme.colors['gray-dark'][8]})`,
              '&:hover': {
                backgroundColor: `light-dark(${theme.colors.gray[2]}, ${theme.colors['gray-dark'][9]})`,
                color: `light-dark(${theme.colors.gray[8]}, ${theme.white})`,
              },
            },
            ...sectionStyles,
          };
        }
        if (props.variant === 'destructive') {
          return {
            root: {
              ...shared,

              backgroundColor: theme.colors.error[6],
              color: theme.white,
              border: `${rem(1)} solid ${theme.colors.error[6]}`,
              '&:hover': { backgroundColor: theme.colors.error[7] },
            },
            ...sectionStyles,
          };
        }
        if (props.variant === 'text') {
          return {
            root: {
              ...shared,

              backgroundColor: 'transparent',
              color: theme.colors.brand[7],
              border: '1px solid transparent',
              '&:hover': { backgroundColor: 'transparent', color: theme.colors.brand[5] },
            },
            ...sectionStyles,
          };
        }
        if (props.variant === 'inverse-text') {
          return {
            root: {
              ...shared,

              backgroundColor: 'transparent',
              color: theme.colors.brand[5],
              border: '1px solid transparent',
              '&:hover': { color: theme.colors.brand[4], backgroundColor: 'transparent' },
            },
            ...sectionStyles,
          };
        }
        if (props.variant === 'inverse-outline') {
          return {
            root: {
              ...shared,

              backgroundColor: 'transparent',
              color: theme.colors.brand[5],
              border: `1px solid ${theme.colors.brand[5]}`,
              '&:hover': {
                color: theme.colors.brand[4],
                backgroundColor: 'transparent',
                border: `1px solid ${theme.colors.brand[4]}`,
              },
            },
            ...sectionStyles,
          };
        }
        if (props.variant === 'black-outline') {
          return {
            root: {
              ...shared,
              backgroundColor: 'transparent',
              color: `light-dark(${theme.colors.gray[8]}, ${theme.white})`,
              border: `1px solid ${theme.colors.gray[4]}`,
              '&:hover': {
                backgroundColor: 'unset',
                color: `light-dark(${theme.colors.gray[8]}, ${theme.white})`,
                borderColor: `light-dark(${theme.colors.gray[5]}, ${theme.colors['gray-dark'][8]})`,
              },
            },
            ...sectionStyles,
          };
        }
        // Fallback
        return { root: {}, ...sectionStyles };
      },
    }),
    Modal: Modal.extend({
      defaultProps: {
        radius: 'sm',
        shadow: 'lg',
        centered: true,
        padding: 'xs',
      },
      styles: (theme: MantineTheme, props: { padding?: MantineSpacing }) => ({
        root: {
          zIndex: '1000 !important',
        },
        inner: {
          zIndex: '1002 !important',
        },
        content: {
          backgroundColor: `light-dark(${theme.white}, ${theme.colors?.['gray-dark']?.[10]}) !important`,
          borderRadius: `${theme.radius?.md} !important`,
          boxShadow: `${theme.shadows?.xl} !important`,
          border: 'none !important',
          overflow: 'hidden !important',
          zIndex: '1003 !important',
        },
        overlay: {
          backgroundColor: `light-dark(rgba(0, 0, 0, 0.4), rgba(255, 255, 255, 0.1)) !important`,
          backdropFilter: 'blur(4px) !important',
          zIndex: '1001 !important',
        },
        header: {
          backgroundColor: `light-dark(${theme.white}, ${theme.colors?.['gray-dark']?.[10]}) !important`,
          minHeight: 'auto !important',
          marginBottom: `${theme.spacing?.sm} !important`,
          alignItems: 'flex-start !important',
        },
        title: {
          fontSize: `${TYPOGRAPHY_SCALE.base} !important`,
          lineHeight: '1.4 !important',
          color: `light-dark(${theme.colors?.gray?.[9]}, ${theme.colors?.['gray-dark']?.[1]}) !important`,
        },
        close: {
          color: `light-dark(${theme.colors?.gray?.[6]}, ${theme.colors?.['gray-dark']?.[4]}) !important`,
          backgroundColor: 'transparent !important',
          border: 'none !important',
          width: `${theme.spacing?.md} !important`,
          height: `${theme.spacing?.md} !important`,
          borderRadius: `${theme.radius?.sm} !important`,
          transition: 'background-color 150ms ease, color 150ms ease !important',
          alignSelf: 'flex-start !important',

          '&:hover': {
            backgroundColor: `light-dark(${theme.colors?.gray?.[1]}, ${theme.colors?.['gray-dark']?.[7]}) !important`,
            color: `light-dark(${theme.colors?.gray?.[9]}, ${theme.colors?.['gray-dark']?.[2]}) !important`,
          },

          '&:focus': {
            outline: `2px solid ${theme.colors?.brand?.[6]} !important`,
            outlineOffset: '2px !important',
          },
        },
        body: {
          fontSize: `${TYPOGRAPHY_SCALE.sm} !important`,
          lineHeight: '1.5 !important',
          color: `light-dark(${theme.colors?.gray?.[7]}, ${theme.colors?.['gray-dark']?.[3]}) !important`,
          fontFamily: `${theme.fontFamily} !important`,
          padding: `${
            props.padding
              ? ((typeof props.padding === 'string'
                  ? theme.spacing?.[props.padding as keyof typeof theme.spacing]
                  : props.padding) ?? theme.spacing?.sm)
              : theme.spacing?.xs
          } !important`,
        },
      }),
    }),
    Input: Input.extend({
      defaultProps: {
        size: 'sm',
      },
      styles: (theme: MantineTheme, props: { variant?: string }) => ({
        wrapper: {
          '&[data-focus]': {
            outline: 'none',
          },
        },
        input: {
          // Dark surfaces use gray-dark 10 / 9 (aligned with Mantine tokens & former alignment CSS)
          backgroundColor:
            props.variant === 'filled'
              ? undefined
              : `light-dark(${theme.white}, ${theme.colors?.['gray-dark']?.[10]})`,
          border: `1px solid light-dark(${theme.colors?.gray?.[3]}, ${theme.colors?.['gray-dark']?.[9]})`,
          borderRadius: theme.radius?.sm,
          color: `light-dark(${theme.colors?.gray?.[9]}, ${theme.colors?.['gray-dark']?.[1]})`,
          fontFamily: theme.fontFamily, // Montserrat
          outline: 'none',

          '&::placeholder': {
            color: `light-dark(${theme.colors?.gray?.[5]}, ${theme.colors?.['gray-dark']?.[5]})`,
          },

          '&:hover:not(:focus):not(:disabled)': {
            borderColor: theme.colors?.brand?.[5], // #9E77ED - lighter purple for hover
          },

          '&:focus, &:focus-visible': {
            borderColor: theme.colors?.brand?.[6], // #7F56D9 - main brand color for focus
            backgroundColor: `light-dark(${theme.white}, ${theme.colors?.['gray-dark']?.[10]})`,
            outline: 'none',
            boxShadow: 'none',
          },

          '&:disabled': {
            backgroundColor: `light-dark(${theme.colors?.gray?.[3]}, ${theme.colors?.['gray-dark']?.[9]})`,
            borderColor: `light-dark(${theme.colors?.gray?.[2]}, ${theme.colors?.['gray-dark']?.[9]})`,
            color: `light-dark(${theme.colors?.gray?.[6]}, ${theme.colors?.['gray-dark']?.[6]})`,
            cursor: 'not-allowed',

            '&::placeholder': {
              color: `light-dark(${theme.colors?.gray?.[6]}, ${theme.colors?.['gray-dark']?.[5]})`,
            },
          },

          '&[data-error]': {
            borderColor: theme.colors?.error?.[5],
            color: theme.colors?.error?.[6],

            '&::placeholder': {
              color: theme.colors?.error?.[5],
            },

            '&:focus, &:focus-visible': {
              borderColor: theme.colors?.error?.[5],
              outline: 'none',
              boxShadow: 'none',
            },
          },
        },
      }),
    }),
    InputWrapper: Input.Wrapper.extend({
      styles: (theme: MantineTheme) => ({
        label: {
          fontFamily: theme.fontFamily, // Montserrat
          fontWeight: 600,
          fontSize: TYPOGRAPHY_SCALE.xs, // 12px
          lineHeight: '16px',
          color: `light-dark(${theme.colors?.gray?.[9]}, ${theme.colors?.['gray-dark']?.[0]})`,
        },
        description: {
          fontFamily: theme.fontFamily, // Montserrat
          fontWeight: 400,
          fontSize: TYPOGRAPHY_SCALE.xxs, // 10px
          lineHeight: '12.5px',
          color: `light-dark(${theme.colors?.gray?.[5]}, ${theme.colors?.['gray-dark']?.[4]})`,
          marginBottom: theme.spacing?.xs, // 4px gap from input container
        },
        error: {
          fontFamily: theme.fontFamily,
          fontWeight: 400,
          fontSize: TYPOGRAPHY_SCALE.xxs,
          lineHeight: '12.5px',
        },
      }),
    }),

    DatePickerInput: DatePickerInput.extend({
      defaultProps: {
        size: 'sm',
        radius: 'sm',
      },
      styles: (theme: MantineTheme) => ({
        input: {
          fontFamily: theme.fontFamily, // Montserrat
          fontSize: TYPOGRAPHY_SCALE.sm, // 14px
          fontWeight: 400,
          borderRadius: theme.radius?.sm,
          borderColor: `light-dark(${theme.colors?.gray?.[4]}, ${theme.colors?.['gray-dark']?.[9]})`,
          backgroundColor: `light-dark(${theme.white}, ${theme.colors?.['gray-dark']?.[10]})`,
          color: `light-dark(${theme.colors?.gray?.[9]}, ${theme.colors?.['gray-dark']?.[2]})`,

          // Hover state
          '&:hover:not([data-disabled])': {
            borderColor: `light-dark(${theme.colors?.brand?.[5]}, ${theme.colors?.brand?.[6]})`,
          },

          // Focus state — border only (no outline / box-shadow ring)
          '&:focus, &:focus-visible, &:focus-within': {
            borderColor: `light-dark(${theme.colors?.brand?.[6]}, ${theme.colors?.brand?.[5]})`,
            outline: 'none',
            boxShadow: 'none',
          },

          // Error state
          '&[data-invalid]': {
            borderColor: `light-dark(${theme.colors?.red?.[6]}, ${theme.colors?.red?.[7]})`,
            color: `light-dark(${theme.colors?.red?.[7]}, ${theme.colors?.red?.[6]})`,

            '&::placeholder': {
              color: `light-dark(${theme.colors?.red?.[6]}, ${theme.colors?.red?.[7]}) !important`,
            },
          },

          // Disabled state
          '&[data-disabled]': {
            backgroundColor: `light-dark(${theme.colors?.gray?.[1]}, ${theme.colors?.['gray-dark']?.[9]})`,
            color: `light-dark(${theme.colors?.gray?.[6]}, ${theme.colors?.['gray-dark']?.[4]})`,
            borderColor: `light-dark(${theme.colors?.gray?.[3]}, ${theme.colors?.['gray-dark']?.[9]})`,
            cursor: 'not-allowed',
          },

          '&::placeholder': {
            color: `light-dark(${theme.colors?.gray?.[6]}, ${theme.colors?.['gray-dark']?.[4]})`,
          },
        },

        // Calendar dropdown styling
        calendar: {
          fontFamily: theme.fontFamily,
        },

        // Calendar header
        calendarHeader: {
          fontFamily: theme.fontFamily,
          fontWeight: 600,
        },

        // Day cells
        day: {
          fontFamily: theme.fontFamily,
          fontSize: TYPOGRAPHY_SCALE.sm,

          // Today highlight
          '&[data-today]': {
            backgroundColor: `light-dark(${theme.colors?.brand?.[0]}, ${theme.colors?.brand?.[9]})`,
            color: `light-dark(${theme.colors?.brand?.[7]}, ${theme.colors?.brand?.[2]})`,
          },

          // Selected day
          '&[data-selected]': {
            backgroundColor: `light-dark(${theme.colors?.brand?.[6]}, ${theme.colors?.brand?.[7]})`,
            color: theme.white,
          },

          // Hover state
          '&:hover:not([data-selected]):not([data-disabled])': {
            backgroundColor: `light-dark(${theme.colors?.gray?.[1]}, ${theme.colors?.['gray-dark']?.[7]})`,
          },
        },
      }),
    }),

    Breadcrumbs: Breadcrumbs.extend({
      defaultProps: {
        separatorMargin: 'xs',
      },
      styles: (theme: MantineTheme) => ({
        root: {
          fontFamily: theme.fontFamily, // Montserrat
          fontSize: TYPOGRAPHY_SCALE.sm, // 14px default
          fontWeight: 400,

          // CSS custom property for different sizes
          '&[data-size="xs"]': {
            fontSize: TYPOGRAPHY_SCALE.xs, // 12px
          },
          '&[data-size="sm"]': {
            fontSize: TYPOGRAPHY_SCALE.sm, // 14px
          },
          '&[data-size="md"]': {
            fontSize: TYPOGRAPHY_SCALE.base, // 16px
          },
          '&[data-size="lg"]': {
            fontSize: TYPOGRAPHY_SCALE.lg, // 18px
          },
          '&[data-size="xl"]': {
            fontSize: TYPOGRAPHY_SCALE.xl, // 20px
          },
        },
        breadcrumb: {
          color: `light-dark(${theme.colors?.gray?.[8]}, ${theme.colors?.['gray-dark']?.[1]})`, // Base colors
          textDecoration: 'none',
          transition: 'color 150ms ease',
          fontFamily: 'inherit',
          fontSize: 'inherit',

          '&:hover': {
            color: `light-dark(${theme.colors?.brand?.[6]}, ${theme.colors?.brand?.[4]})`, // Lighter hover colors
            textDecoration: 'none',
          },

          '&[data-active="true"]': {
            color: `light-dark(${theme.colors?.brand?.[9]}, ${theme.colors?.brand?.[6]})`, // Active colors
            fontWeight: 500,
          },
        },
        separator: {
          color: `light-dark(${theme.colors?.gray?.[4]}, ${theme.colors?.['gray-dark']?.[5]})`, // Separator colors
        },
      }),
    }),
    Tabs: Tabs.extend({
      defaultProps: {
        variant: 'default',
      },
      styles: (theme: MantineTheme, props: { variant?: string }) => {
        const variant = props.variant ?? 'default';
        const tabRule = `light-dark(${theme.colors?.gray?.[2]}, ${theme.colors?.['gray-dark']?.[6]})`;
        const tabInkMuted = `light-dark(${theme.colors?.gray?.[6]}, ${theme.colors?.['gray-dark']?.[4]})`;
        const tabInk = `light-dark(${theme.colors?.gray?.[9]}, ${theme.colors?.['gray-dark']?.[0]})`;
        const tabInkHover = `light-dark(${theme.colors?.gray?.[8]}, ${theme.colors?.['gray-dark']?.[2]})`;
        const tabIndicator = `light-dark(${theme.colors?.brand?.[9]}, ${theme.colors?.brand?.[5]})`;
        const focusRing = `light-dark(${theme.colors?.brand?.[4]}, ${theme.colors?.brand?.[5]})`;
        const panelSurface = `light-dark(${theme.white}, ${theme.colors?.['gray-dark']?.[10]})`;
        const outlineRule = `light-dark(${theme.colors?.gray?.[2]}, ${theme.colors?.['gray-dark']?.[6]})`;

        const disabledTab = {
          color: `light-dark(${theme.colors?.gray?.[4]}, ${theme.colors?.['gray-dark']?.[6]})`,
          cursor: 'not-allowed',
          opacity: 0.55,

          '&:hover': {
            color: `light-dark(${theme.colors?.gray?.[4]}, ${theme.colors?.['gray-dark']?.[6]})`,
            backgroundColor: 'transparent',
          },
        };

        const focusVisible = {
          outline: `2px solid ${focusRing}`,
          outlineOffset: rem(2),
        };

        const shared = {
          root: { fontFamily: theme.fontFamily },
          panel: { padding: `${theme.spacing?.lg} 0px` },
          tabSection: { marginRight: theme.spacing.xs },
          list: {
            display: 'inline-flex',
            width: 'fit-content',
          },
        };

        if (variant === 'default') {
          return {
            ...shared,
            list: {
              ...shared.list,
              gap: rem(2),
              borderBottom: `1px solid ${tabRule}`,
            },
            tab: {
              fontSize: TYPOGRAPHY_SCALE.sm,
              fontWeight: 400,
              padding: `${theme.spacing?.md} ${theme.spacing?.xl}`,
              marginBottom: rem(-1),
              color: tabInkMuted,
              backgroundColor: 'transparent',
              border: 'none',
              borderBottomWidth: rem(2),
              borderBottomStyle: 'solid',
              borderBottomColor: 'transparent',
              borderRadius: 0,
              transition: 'color 150ms ease, border-color 150ms ease',

              '&:hover:not([data-active]):not([data-disabled])': {
                color: tabInkHover,
                backgroundColor: 'transparent',
              },

              '&:focus-visible': focusVisible,

              '&[data-active]': {
                color: tabInk,
                fontWeight: 500,
                borderBottomColor: tabIndicator,
                backgroundColor: 'transparent',

                '&:hover': {
                  color: tabInk,
                  backgroundColor: 'transparent',
                },
              },

              '&[data-disabled]': disabledTab,

              '&[data-orientation="vertical"]': {
                marginBottom: 0,
                borderBottomWidth: 0,
                borderRightWidth: rem(2),
                borderRightStyle: 'solid',
                borderRightColor: 'transparent',

                '&[data-active]': {
                  borderBottomColor: 'transparent',
                  borderRightColor: tabIndicator,
                },
              },
            },
          };
        }

        if (variant === 'outline') {
          return {
            ...shared,
            root: {
              ...shared.root,
              '--tabs-list-border-width': '0px',
            },
            list: {
              ...shared.list,
              gap: 0,
            },
            tab: {
              fontSize: TYPOGRAPHY_SCALE.sm,
              fontWeight: 400,
              padding: `${theme.spacing?.md} ${theme.spacing?.xl}`,
              color: tabInkMuted,
              backgroundColor: 'transparent',
              position: 'relative',
              marginLeft: rem(-1),
              marginRight: rem(-1),
              borderTop: `1px solid transparent`,
              borderLeft: `1px solid transparent`,
              borderRight: `1px solid transparent`,
              borderBottom: `1px solid ${outlineRule}`,
              borderRadius: `${theme.radius.sm} ${theme.radius.sm} 0 0`,
              transition: 'color 150ms ease, background-color 150ms ease, border-color 150ms ease',

              '&:hover:not([data-active]):not([data-disabled])': {
                color: tabInkHover,
              },

              '&:focus-visible': focusVisible,

              '&[data-active]': {
                color: tabInk,
                fontWeight: 500,
                borderTop: `1px solid ${outlineRule}`,
                borderLeft: `1px solid ${outlineRule}`,
                borderRight: `1px solid ${outlineRule}`,
                borderBottom: 'none',
                backgroundColor: panelSurface,
                marginBottom: rem(1),
                marginLeft: 0,
                marginRight: 0,
                zIndex: 2,
              },

              '&[data-disabled]': disabledTab,

              '&[data-orientation="vertical"]': {
                borderBottom: `1px solid transparent`,
                marginTop: rem(-1),
                marginBottom: rem(-1),
                marginLeft: 0,
                marginRight: 0,

                '&[data-placement="left"], &:not([data-placement])': {
                  borderLeft: `1px solid transparent`,
                  borderRight: `1px solid ${outlineRule}`,
                  borderRadius: `${theme.radius.sm} 0 0 ${theme.radius.sm}`,

                  '&[data-active]': {
                    borderTop: `1px solid ${outlineRule}`,
                    borderLeft: `1px solid ${outlineRule}`,
                    borderBottom: `1px solid ${outlineRule}`,
                    borderRight: 'none',
                    backgroundColor: panelSurface,
                    marginRight: rem(1),
                    marginTop: 0,
                    marginBottom: 0,
                    zIndex: 2,
                  },
                },

                '&[data-placement="right"]': {
                  borderLeft: `1px solid ${outlineRule}`,
                  borderRight: `1px solid transparent`,
                  borderRadius: `0 ${theme.radius.sm} ${theme.radius.sm} 0`,

                  '&[data-active]': {
                    borderTop: `1px solid ${outlineRule}`,
                    borderRight: `1px solid ${outlineRule}`,
                    borderBottom: `1px solid ${outlineRule}`,
                    borderLeft: 'none',
                    backgroundColor: panelSurface,
                    marginLeft: rem(1),
                    marginTop: 0,
                    marginBottom: 0,
                    zIndex: 2,
                  },
                },
              },
            },
          };
        }

        if (variant === 'pills') {
          return {
            ...shared,
            list: {
              ...shared.list,
              gap: rem(4),
              padding: rem(2),
              borderRadius: theme.radius.sm,
              backgroundColor: `light-dark(${theme.colors?.gray?.[1]}, ${theme.colors?.['gray-dark']?.[9]})`,
            },
            tab: {
              fontSize: TYPOGRAPHY_SCALE.sm,
              fontWeight: 400,
              padding: `${theme.spacing?.xs} ${theme.spacing?.lg}`,
              color: tabInkMuted,
              backgroundColor: 'transparent',
              border: 'none',
              borderRadius: theme.radius.sm,
              transition: 'color 150ms ease, background-color 150ms ease',

              '&:hover:not([data-active]):not([data-disabled])': {
                color: tabInkHover,
                backgroundColor: `light-dark(${theme.colors?.gray?.[2]}, ${theme.colors?.['gray-dark']?.[8]})`,
              },

              '&:focus-visible': focusVisible,

              '&[data-active]': {
                color: theme.white,
                fontWeight: 500,
                backgroundColor: tabIndicator,
                boxShadow: theme.shadows?.xs,

                '&:hover': {
                  color: theme.white,
                  backgroundColor: `light-dark(${theme.colors?.brand?.[8]}, ${theme.colors?.brand?.[4]})`,
                },
              },

              '&[data-disabled]': disabledTab,
            },
          };
        }

        return shared;
      },
    }),
    Stepper: Stepper.extend({
      styles: (theme: MantineTheme) => ({
        root: {
          fontFamily: theme.fontFamily, // Montserrat
        },
        step: {
          padding: `0 ${theme.spacing.xs}`,
          borderRadius: theme.radius.md,
          transition: 'background-color 150ms ease, color 150ms ease',
          cursor: 'pointer',

          '&[data-allow-click="false"]': {
            cursor: 'default',
          },

          '&:hover[data-allow-click="true"]': {
            backgroundColor: `light-dark(${theme.colors?.gray?.[1]}, ${theme.colors?.['gray-dark']?.[7]})`,
          },
        },
        stepIcon: {
          border: `2px solid transparent`,
          backgroundColor: `light-dark(${theme.colors?.gray?.[1]}, ${theme.colors?.['gray-dark']?.[8]})`,
          color: `light-dark(${theme.colors?.gray?.[9]}, ${theme.colors?.['gray-dark']?.[4]})`,
          transition: 'all 150ms ease',

          // Progress state (when loading)
          '&[data-progress="true"]': {
            borderColor: theme.colors?.brand?.[7],
            backgroundColor: `light-dark(${theme.colors?.gray?.[1]}, ${theme.colors?.['gray-dark']?.[8]})`,
            color: `light-dark(${theme.colors?.gray?.[9]}, ${theme.colors?.['gray-dark']?.[4]})`,
          },

          // Active state
          '&[data-active="true"]': {
            backgroundColor: `light-dark(${theme.colors?.brand?.[9]}, ${theme.colors?.brand?.[6]})`,
            borderColor: `light-dark(${theme.colors?.brand?.[9]}, ${theme.colors?.brand?.[6]})`,
            color: theme.white,
          },

          // Completed state
          '&[data-completed="true"]': {
            backgroundColor: theme.colors?.brand?.[9],
            borderColor: theme.colors?.brand?.[9],
            color: theme.white,
          },
        },
        stepCompletedIcon: {
          color: theme.white,
        },
        stepBody: {
          marginLeft: theme.spacing.md,
        },
        stepLabel: {
          fontSize: TYPOGRAPHY_SCALE.sm, // 14px
          fontWeight: 400,
          lineHeight: '20px',
          color: `light-dark(${theme.colors?.gray?.[9]}, ${theme.colors?.['gray-dark']?.[1]})`,
          fontFamily: theme.fontFamily, // Montserrat

          // Completed state
          '[data-completed="true"] &': {
            color: `light-dark(${theme.colors?.gray?.[9]}, ${theme.colors?.['gray-dark']?.[1]})`,
          },

          // Active state
          '[data-active="true"] &': {
            color: `light-dark(${theme.colors?.brand?.[9]}, ${theme.colors?.brand?.[4]})`,
            fontWeight: 600,
          },
        },
        stepDescription: {
          fontSize: TYPOGRAPHY_SCALE.xs, // 12px
          fontWeight: 400,
          lineHeight: '16px',
          color: `light-dark(${theme.colors?.gray?.[6]}, ${theme.colors?.['gray-dark']?.[4]})`,
          fontFamily: theme.fontFamily, // Montserrat
          marginTop: rem(2),

          // Active state
          '[data-active="true"] &': {
            color: `light-dark(${theme.colors?.gray?.[7]}, ${theme.colors?.['gray-dark']?.[3]})`,
          },
        },
        separator: {
          backgroundColor: `light-dark(${theme.colors?.gray?.[3]}, ${theme.colors?.['gray-dark']?.[6]})`,

          // Completed state (when previous step is completed)
          '&[data-active="true"]': {
            backgroundColor: theme.colors?.brand?.[9],
          },
        },
        verticalSeparator: {
          backgroundColor: `light-dark(${theme.colors?.gray?.[3]}, ${theme.colors?.['gray-dark']?.[6]})`,

          // Completed state (when previous step is completed)
          '&[data-active="true"]': {
            backgroundColor: theme.colors?.brand?.[9],
          },
        },

        stepLoader: {
          color: `light-dark(${theme.colors?.brand?.[7]}, ${theme.colors?.brand?.[4]})`,
        },
      }),
    }),
    Alert: Alert.extend({
      defaultProps: {
        radius: 'sm',
        withCloseButton: false,
      },
      styles: (theme, props) => ({
        root: {
          fontSize: theme.fontSizes.sm,
          fontWeight: 400,
          '&&& .mantine-Text-root': {
            fontSize: `${theme.fontSizes.sm}`,
            lineHeight: '20px',
          },
          ...(props.variant === 'warning' && {
            backgroundColor: theme.colors.warning[7],
            color: theme.white,
            '& .mantine-Text-root': { color: theme.white },
          }),

          ...(props.variant === 'info' && {
            backgroundColor: theme.colors.brand[9],
            color: theme.white,
            '& .mantine-Text-root': { color: theme.white },
          }),

          ...(props.variant === 'success' && {
            backgroundColor: theme.colors.success[7],
            color: theme.white,
            '& .mantine-Text-root': { color: theme.white },
          }),

          ...(props.variant === 'error' && {
            backgroundColor: theme.colors.error[7],
            color: theme.white,
            '&& .mantine-Text-root': { color: theme.white },
          }),
        },

        closeButton: {
          ...((props.variant === 'warning' ||
            props.variant === 'info' ||
            props.variant === 'success' ||
            props.variant === 'error') && {
            color: theme.white,
          }),
        },

        title: {
          fontSize: theme.fontSizes.md,
          fontWeight: 600,
        },
        message: {
          fontSize: theme.fontSizes.sm,
          fontWeight: 400,
          ...(['warning', 'info', 'success', 'error'].includes(props.variant || '')
            ? { color: theme.white }
            : {}),
        },
      }),
    }),
    Notification: Notification.extend({
      defaultProps: {
        variant: 'default',
      },

      vars: (theme, props: ExtendedNotificationProps) => {
        const semantic = props.semanticVariant ?? 'default';
        const variantKey = String(props.variant);
        const isLegacySupport = ['success', 'error', 'warning', 'info'].includes(variantKey);

        const baseColorMap: Record<string, string> = {
          success: theme.colors.green?.[6],
          error: theme.colors.red?.[6],
          loading: theme.colors.blue?.[6],
          info: theme.colors.brand?.[9],
          warning: theme.colors.yellow?.[6],
          default: theme.colors.brand?.[9],
        };

        const colorKey = isLegacySupport ? variantKey : semantic;

        return {
          root: {
            '--notification-color': baseColorMap[colorKey],
            '--notification-text-color': theme.colors.dark[9],
            '--notification-border-color': props.grayBorder
              ? theme.colors['gray-dark'][2]
              : baseColorMap[colorKey],
            '--notification-bg-color': `light-dark(${theme.white}, ${theme.black})`,
          },
        };
      },

      styles: (theme, props: ExtendedNotificationProps) => {
        const { variant = 'default', semanticVariant = 'default', withBorder = false } = props;

        const variantKey = String(variant);
        const isLegacySupport = ['success', 'error', 'warning', 'info'].includes(variantKey);
        const colorKey = isLegacySupport ? variantKey : semanticVariant;

        const filledBgColorMap: Record<string, string> = {
          success: theme.colors.green[3],
          error: theme.colors.red[2],
          loading: theme.colors.blue[2],
          info: theme.colors.gray[1],
          warning: theme.colors.yellow[1],
          default: theme.colors.gray[1],
        };

        const filledBgColor = filledBgColorMap[colorKey] ?? theme.colors.gray[1];

        const base = {
          width: '380px',
          padding: '1rem',
          fontSize: theme.fontSizes.sm,
          fontWeight: 400,
          borderRadius: theme.radius.sm,
          color: theme.colors.dark[9],
          border: withBorder ? '1px solid var(--notification-border-color)' : 'none',
        };

        const variantStyles: Record<string, unknown> = {
          filled: {
            backgroundColor: filledBgColor,
          },
          outline: {
            backgroundColor: 'transparent',
            color: 'var(--notification-color)',
            border: '1px solid var(--notification-border-color)',
          },
          // legacy variants — override background directly
          success: { backgroundColor: filledBgColorMap.success },
          error: { backgroundColor: filledBgColorMap.error },
          warning: { backgroundColor: filledBgColorMap.warning },
          info: { backgroundColor: filledBgColorMap.info },
        };

        return {
          root: {
            ...base,
            ...(variantStyles[variantKey] || {}),
            '&::before': {
              backgroundColor: 'var(--notification-color)',
              marginTop: '3px',
              marginBottom: '3px',
              marginLeft: '2px',
              width: '4px',
            },
          },

          icon: {
            backgroundColor: 'var(--notification-color)',
            color:
              semanticVariant === 'warning'
                ? `light-dark(${theme.black}, ${theme.white})`
                : `light-dark(${theme.white}, ${theme.black})`,
          },

          title: {
            fontWeight: 600,
            color: ['filled', 'success', 'error', 'warning', 'info'].includes(variant)
              ? theme.black
              : undefined,
          },

          description: {
            fontSize: theme.fontSizes.sm,
            wordWrap: 'break-word',
            color: ['filled', 'success', 'error', 'warning', 'info'].includes(variant)
              ? theme.colors.gray[6]
              : undefined,
          },

          closeButton: {
            alignSelf: 'center',
            color: theme.colors.gray[6],
            '&:hover': {
              background: 'none',
            },
          },
        };
      },
    }),

    Popover: Popover.extend({
      styles: (theme: MantineTheme) => ({
        dropdown: {
          backgroundColor: `light-dark(${theme.white}, ${theme.colors?.['gray-dark']?.[10]})`,
          border: `1px solid light-dark(${theme.colors?.gray?.[3]}, ${theme.colors?.['gray-dark']?.[9]})`,
          borderRadius: theme.radius?.sm,
          boxShadow: theme.shadows?.lg,
          fontFamily: theme.fontFamily, // Montserrat font family
          color: `light-dark(${theme.colors?.gray?.[9]}, ${theme.colors?.['gray-dark']?.[2]})`,
          padding: `${theme.spacing.sm} ${theme.spacing.md}`,

          // Ensure proper text styling
          '& .mantine-Text-root, & p, & span:not([class*="icon"])': {
            fontFamily: 'inherit !important',
            color: 'inherit',
          },

          // Form elements inside popover
          '& .mantine-Input-input': {
            backgroundColor: `light-dark(${theme.white}, ${theme.colors?.['gray-dark']?.[10]})`,
            borderColor: `light-dark(${theme.colors?.gray?.[3]}, ${theme.colors?.['gray-dark']?.[9]})`,
            color: `light-dark(${theme.colors?.gray?.[9]}, ${theme.colors?.['gray-dark']?.[2]})`,

            '&:focus, &:focus-visible': {
              borderColor: theme.colors?.brand?.[6],
              outline: 'none',
              boxShadow: 'none',
            },
          },

          // Buttons inside popover
          '& .mantine-Button-root': {
            fontFamily: 'inherit !important',
          },
        },

        arrow: {
          border: `1px solid light-dark(${theme.colors?.gray?.[3]}, ${theme.colors?.['gray-dark']?.[9]})`,
          backgroundColor: `light-dark(${theme.white}, ${theme.colors?.['gray-dark']?.[10]})`,
        },
      }),
    }),
    Tooltip: Tooltip.extend({
      defaultProps: {
        arrowSize: 8,
        withArrow: true,
        withinPortal: true,
        zIndex: 10000,
      },
      styles: (theme: MantineTheme) => {
        // Default colors with proper light/dark contrast
        // Light mode: dark background, light text
        // Dark mode: light background, dark text
        return {
          tooltip: {
            backgroundColor:
              'light-dark(var(--mantine-color-gray-dark-11), var(--mantine-color-gray-dark-0))',
            color:
              'light-dark(var(--mantine-color-gray-dark-0), var(--mantine-color-gray-dark-11))',
            border: 'none',
            borderRadius: theme.radius?.sm,
            boxShadow: theme.shadows?.lg,
            fontFamily: theme.fontFamily, // Montserrat font family
            fontSize: TYPOGRAPHY_SCALE.xs, // 12px
            lineHeight: '1.4',
            padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
            maxWidth: rem(300),
            minWidth: rem(120),
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            wordWrap: 'break-word',
            textWrap: 'pretty',
            textAlign: 'center',
            zIndex: 10000,

            // Ensure proper text styling
            '& *': {
              fontFamily: 'inherit !important',
              color: 'inherit',
              whiteSpace: 'inherit',
              wordBreak: 'inherit',
              wordWrap: 'inherit',
            },
          },

          arrow: {
            border: 'none',
            backgroundColor:
              'light-dark(var(--mantine-color-gray-dark-11), var(--mantine-color-gray-dark-0))',
          },
        };
      },
    }),
    Menu: Menu.extend({
      styles: (theme: MantineTheme) => ({
        dropdown: {
          backgroundColor: `light-dark(${theme.white}, ${theme.colors?.['gray-dark']?.[8]})`,
          border: `1px solid light-dark(${theme.colors?.gray?.[3]}, ${theme.colors?.['gray-dark']?.[6]})`,
          borderRadius: theme.radius?.sm,
          boxShadow: theme.shadows?.lg,
          fontFamily: theme.fontFamily, // Montserrat font family
          padding: theme.spacing.xs,
          minWidth: rem(160),
        },

        arrow: {
          border: `1px solid light-dark(${theme.colors?.gray?.[3]}, ${theme.colors?.['gray-dark']?.[6]})`,
          backgroundColor: `light-dark(${theme.white}, ${theme.colors?.['gray-dark']?.[8]})`,
        },

        item: {
          fontSize: TYPOGRAPHY_SCALE.sm, // 14px
          padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
          borderRadius: theme.radius?.xs,
          color: `light-dark(${theme.colors?.gray?.[9]}, ${theme.colors?.['gray-dark']?.[2]})`,
          fontFamily: theme.fontFamily,

          '&:hover': {
            backgroundColor: `light-dark(${theme.colors?.gray?.[1]}, ${theme.colors?.['gray-dark']?.[7]})`,
          },

          '&[data-hovered="true"]': {
            backgroundColor: `light-dark(${theme.colors?.gray?.[1]}, ${theme.colors?.['gray-dark']?.[7]})`,
          },

          '&[data-disabled="true"]': {
            color: `light-dark(${theme.colors?.gray?.[5]}, ${theme.colors?.['gray-dark']?.[5]})`,
            cursor: 'not-allowed',
          },

          // Color variants for items
          '&[data-color="red"]': {
            color: theme.colors?.red?.[6],
            '&:hover': {
              backgroundColor: `light-dark(${theme.colors?.red?.[0]}, ${theme.colors?.red?.[9]})`,
            },
          },
        },

        itemLabel: {
          color: 'inherit',
          fontFamily: 'inherit',
        },

        itemSection: {
          color: `light-dark(${theme.colors?.gray?.[6]}, ${theme.colors?.['gray-dark']?.[4]})`,

          // Ensure icons inherit proper styling
          '& *': {
            color: 'inherit',
          },
        },

        label: {
          fontSize: TYPOGRAPHY_SCALE.xs, // 12px
          fontWeight: 600,
          color: `light-dark(${theme.colors?.gray?.[6]}, ${theme.colors?.['gray-dark']?.[4]})`,
          padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
          fontFamily: theme.fontFamily,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
        },

        divider: {
          borderColor: `light-dark(${theme.colors?.gray?.[3]}, ${theme.colors?.['gray-dark']?.[6]})`,
          margin: `${theme.spacing.xs} 0`,
        },

        chevron: {
          color: `light-dark(${theme.colors?.gray?.[6]}, ${theme.colors?.['gray-dark']?.[4]})`,
        },

        // Submenu specific styling
        sub: {
          // Submenu dropdown
          '& .mantine-Menu-dropdown': {
            transform: 'translateX(-16px)', // Slight offset for better alignment
          },

          // Submenu target (parent menu item) when expanded
          '&[data-expanded="true"] .mantine-Menu-item': {
            backgroundColor: `light-dark(${theme.colors?.gray?.[1]}, ${theme.colors?.['gray-dark']?.[7]}) !important`,
          },
        },
      }),
    }),
    Table: Table.extend({
      defaultProps: {
        striped: false,
        highlightOnHover: false,
        horizontalSpacing: 'md',
        verticalSpacing: 'sm',
        withTableBorder: true,
        withColumnBorders: true,
        withRowBorders: true,
      },
      styles: (theme: MantineTheme) => ({
        table: {
          fontFamily: theme.fontFamily, // Montserrat
          fontSize: TYPOGRAPHY_SCALE.sm, // 14px
          lineHeight: '20px',
          borderCollapse: 'collapse',
          // Override table border color to match our design using light-dark function
          borderColor: `light-dark(${theme.colors.gray[4]}, ${theme.colors['gray-dark'][6]}) !important`,
        },
        tbody: {
          // Apply row borders via cells for better compatibility
          '& tr:not(:last-child) td': {
            borderBottom: `1px solid light-dark(${theme.colors.gray[4]}, ${theme.colors['gray-dark'][6]})`,
          },

          // Hover effects for rows
          '& tr': {
            transition: 'background-color 150ms ease',

            '&:hover': {
              backgroundColor: `light-dark(${theme.colors.gray[2]}, ${theme.colors['gray-dark'][8]})`,
            },
          },
        },
        thead: {
          borderBottom: `2px solid light-dark(${theme.colors.gray[4]}, ${theme.colors['gray-dark'][5]})`,
        },
        th: {
          fontSize: TYPOGRAPHY_SCALE.xs, // 12px
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          paddingLeft: theme.spacing.md,
          paddingRight: theme.spacing.md,
          textAlign: 'left',
          whiteSpace: 'nowrap',
          // Ensure header borders use consistent colors
          borderColor: `light-dark(${theme.colors.gray[4]}, ${theme.colors['gray-dark'][6]})`,
        },
        td: {
          paddingLeft: theme.spacing.md,
          paddingRight: theme.spacing.md,
          fontSize: TYPOGRAPHY_SCALE.sm, // 14px
          lineHeight: '20px',
          verticalAlign: 'middle',
          // Ensure cell borders use consistent colors
          borderColor: `light-dark(${theme.colors.gray[4]}, ${theme.colors['gray-dark'][6]})`,

          // Special styling for action cells
          '&[data-action="true"]': {
            width: '1%',
            whiteSpace: 'nowrap',
            textAlign: 'right',
          },

          // Status badge styling
          '& .mantine-Badge-root': {
            fontSize: TYPOGRAPHY_SCALE.xxs,
          },
        },
        tfoot: {
          '& th, & td': {
            fontSize: TYPOGRAPHY_SCALE.xs, // 12px
            fontWeight: 600,
            paddingLeft: theme.spacing.md,
            paddingRight: theme.spacing.md,
            paddingTop: theme.spacing.xs,
            paddingBottom: theme.spacing.xs,
            // Ensure footer borders use consistent colors
            borderColor: `light-dark(${theme.colors.gray[4]}, ${theme.colors['gray-dark'][6]})`,
          },
        },
        caption: {
          fontSize: TYPOGRAPHY_SCALE.xs, // 12px
          padding: `${theme.spacing.sm} ${theme.spacing.md}`,
          textAlign: 'center',
          fontStyle: 'italic',
        },
      }),
    }),
    ActionIcon: {
      defaultProps: {
        variant: 'transparent',
      },
    },
    Card: Card.extend({
      defaultProps: {
        variant: 'surface',
        withBorder: true,
      },
      styles: (theme: MantineTheme, props: CardProps) => {
        const isSurface = props.variant === 'surface';
        const isSurfaceDark = props.variant === 'surface-dark';
        const { withBorder } = props;

        return {
          root: {
            ...(isSurface && {
              backgroundColor: `light-dark(${theme.white}, ${theme.colors['gray-dark'][10]})`,
              ...(withBorder && {
                border: `1px solid light-dark(${theme.colors.gray?.[3]}, ${theme.colors['gray-dark'][9]})`,
              }),
            }),
            ...(isSurfaceDark && {
              backgroundColor: theme.colors['gray-dark'][10],
              ...(withBorder && {
                border: `1px solid ${theme.colors['gray-dark'][9]}`,
              }),
            }),
          },
        };
      },
    }),
    Avatar: Avatar.extend({
      defaultProps: {
        size: 'md',
        radius: 'xl',
        color: 'initials',
      },
      styles: (theme: MantineTheme, props: { variant?: string; color?: string }) => {
        const isLight = props.variant === 'light';
        const isFilled = props.variant === 'filled';
        const isOutline = props.variant === 'outline';
        const colorKey = props.color === 'initials' ? 'brand' : props.color || 'gray';

        return {
          root: {
            fontFamily: theme.fontFamily,
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            lineHeight: 0,
            // Light variant
            ...(isLight && {
              backgroundColor: `light-dark(${theme.colors?.brand?.[1]}, ${theme.colors?.['gray-dark']?.[2]})`,
              color: `light-dark(${theme.colors?.gray?.[9]}, ${theme.colors?.['gray-dark']?.[8]})`,
              border: 'none',
            }),
            // Filled variant
            ...(isFilled && {
              backgroundColor: `light-dark(${theme.colors?.brand?.[9]}, ${theme.colors?.brand?.[7]})`,
              color: theme.white,
              border: 'none',
            }),
            // Outline variant
            ...(isOutline && {
              backgroundColor: `light-dark(${theme.white}, ${theme.colors?.['gray-dark']?.[2]})`,
              color: `light-dark(${theme.colors?.gray?.[9]}, ${theme.colors?.['gray-dark']?.[8]})`,
              border: `1px solid light-dark(${theme.colors?.gray?.[3]}, ${theme.colors?.['gray-dark']?.[8]})`,
            }),
            // Default variant fallback
            ...(!isLight &&
              !isFilled &&
              !isOutline && {
                backgroundColor: `light-dark(${theme.colors?.[colorKey]?.[1]}, ${theme.colors?.['gray-dark']?.[2]})`,
                border: `1px solid light-dark(${theme.colors?.[colorKey]?.[3]}, ${theme.colors?.['gray-dark']?.[8]})`,
                color: `light-dark(${theme.colors?.[colorKey]?.[7]}, ${theme.colors?.['gray-dark']?.[3]})`,
              }),
          },
          placeholder: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            // Light variant placeholder
            ...(isLight && {
              color: `light-dark(${theme.colors?.gray?.[9]}, ${theme.colors?.['gray-dark']?.[8]})`,
              backgroundColor: 'transparent',
            }),
            // Filled variant placeholder
            ...(isFilled && {
              color: theme.white,
              backgroundColor: 'transparent',
            }),
            // Outline variant placeholder
            ...(isOutline && {
              color: `light-dark(${theme.colors?.gray?.[9]}, ${theme.colors?.['gray-dark']?.[8]})`,
              backgroundColor: 'transparent',
            }),
            // Default placeholder
            ...(!isLight &&
              !isFilled &&
              !isOutline && {
                color: `light-dark(${theme.colors?.[colorKey]?.[6]}, ${theme.colors?.['gray-dark']?.[8]})`,
                backgroundColor: `light-dark(${theme.colors?.[colorKey]?.[1]}, ${theme.colors?.['gray-dark']?.[2]})`,
              }),
          },
          image: {
            objectFit: 'cover',
            objectPosition: 'center',
          },
        };
      },
    }),

    Paper: Paper.extend({
      defaultProps: {
        variant: 'surface',
        withBorder: true,
      },
      styles: (theme: MantineTheme, props: PaperProps) => {
        const isSwitch = props.variant === 'switch';
        const isSurface = props.variant === 'surface';
        const isNeutral = props.variant === 'neutral';
        const isDefault = props.variant === 'default';
        const isSurfaceDark = props.variant === 'surface-dark';
        const { withBorder } = props;

        return {
          root: {
            padding: '1rem',

            ...(isSurface && {
              backgroundColor: `light-dark(${theme.white}, ${theme.colors['gray-dark'][10]})`,
              ...(withBorder && {
                border: `1px solid light-dark(${theme.colors.gray[3]}, ${theme.colors['gray-dark'][9]})`,
              }),
            }),

            ...(isSurfaceDark && {
              backgroundColor: `${theme.colors['gray-dark'][10]}`,
              ...(withBorder && {
                border: `1px solid ${theme.colors['gray-dark'][9]}`,
              }),
            }),

            ...(isNeutral && {
              backgroundColor: `light-dark(${theme.colors.gray[1]}, ${theme.colors.gray[8]})`,
              ...(withBorder && {
                border: `1px solid light-dark(${theme.colors.gray[3]}, ${theme.colors.gray[6]})`,
              }),
            }),

            ...(isDefault && {
              ...(withBorder && {
                border: `1px solid light-dark(${theme.colors.gray[3]}, ${theme.colors.gray[6]})`,
              }),
            }),

            ...(isSwitch && {
              background: `light-dark(${theme.colors.gray[3]}, ${theme.colors.gray[7]})`,
              ...(withBorder && {
                border: `2px solid ${theme.colors.gray[2]}`,
              }),
            }),

            // Optional: fallback if no variant
            ...(!props.variant && {
              backgroundColor: `light-dark(${theme.white}, ${theme.black})`,
              ...(withBorder && {
                border: `1px solid light-dark(${theme.colors.gray[2]}, ${theme.colors.gray[7]})`,
              }),
            }),
          },
        };
      },
    }),

    Combobox: Combobox.extend({
      defaultProps: {
        size: 'xs',
      },
      styles: () => ({
        dropdown: {
          padding: `${theme.spacing?.xs} ${theme.spacing?.none} !important`,
        },

        option: {
          padding: `${theme.spacing?.sm} ${theme.spacing?.md}`,
        },
      }),
    }),
    Timeline: Timeline.extend({
      defaultProps: {
        bulletSize: 24,
        lineWidth: 4,
        align: 'left',
        active: -1,
      },
      styles: (theme: MantineTheme, props: { active?: number; align?: 'left' | 'right' }) => {
        return {
          root: {
            position: 'relative',
          },
          item: {
            position: 'relative',
            paddingBottom: rem(24),

            // Left alignment
            ...(props.align === 'left' && {
              paddingLeft: rem(36),
              textAlign: 'left',
            }),

            // Right alignment
            ...(props.align === 'right' && {
              paddingRight: rem(36),
              textAlign: 'right',
            }),

            '&:last-of-type': {
              paddingBottom: 0,
            },
          },
          itemBullet: {
            position: 'absolute',
            top: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',

            // Default state (future/pending steps)
            border: `4px solid light-dark(${theme.colors.gray[3]}, ${theme.colors['gray-dark'][6]})`,
            backgroundColor: `light-dark(${theme.colors.gray[2]}, ${theme.colors['gray-dark'][8]})`,
            color: `light-dark(${theme.colors.gray[6]}, ${theme.colors['gray-dark'][4]})`,

            // Left alignment positioning
            ...(props.align === 'left' && {
              left: 0,
            }),

            // Right alignment positioning
            ...(props.align === 'right' && {
              right: 0,
            }),

            // Completed state (items before active index)
            '&[data-with-child]': {
              backgroundColor: theme.colors.brand[9],
              borderColor: theme.colors.brand[9],
              color: theme.white,
            },

            // Current state (item at active index) - gray background + purple border
            '&[data-active]': {
              borderColor: theme.colors.brand[9],
              color: `light-dark(${theme.colors.gray[7]}, ${theme.colors['gray-dark'][3]})`,
              backgroundColor: `light-dark(${theme.colors.gray[2]}, ${theme.colors['gray-dark'][8]})`,
            },
          },
          itemLine: {
            position: 'absolute',
            width: rem(2),
            backgroundColor: `light-dark(${theme.colors.gray[3]}, ${theme.colors['gray-dark'][6]})`,

            // Left alignment line positioning
            ...(props.align === 'left' && {
              left: rem(11),
              top: rem(24),
              bottom: rem(-4),
            }),

            // Right alignment line positioning
            ...(props.align === 'right' && {
              right: rem(11),
              top: rem(24),
              bottom: rem(-4),
            }),

            // Completed state line (items before active index)
            '&[data-with-child]': {
              backgroundColor: theme.colors.brand[9],
            },

            // Current state line (item at active index) - dotted purple line
            '&[data-active]': {
              backgroundColor: 'transparent',
              borderLeft: `2px dotted ${theme.colors.brand[9]}`,
              width: 0,
            },
          },
          itemContent: {
            paddingTop: rem(2),

            // Left alignment content
            ...(props.align === 'left' && {
              textAlign: 'left',
            }),

            // Right alignment content
            ...(props.align === 'right' && {
              textAlign: 'right',
            }),
          },
          itemTitle: {
            fontWeight: 600,
            fontSize: theme.fontSizes.sm,
            lineHeight: 1.4,
            marginBottom: rem(4),
            color: `light-dark(${theme.colors.gray[9]}, ${theme.colors['gray-dark'][1]})`,
          },
          itemBody: {
            color: `light-dark(${theme.colors.gray[7]}, ${theme.colors['gray-dark'][3]})`,
            fontSize: theme.fontSizes.sm,
            lineHeight: 1.5,
          },
        };
      },
    }),
    Accordion: Accordion.extend({
      defaultProps: {
        variant: 'default',
        chevronPosition: 'right',
      },
      styles: (theme: MantineTheme, props: AccordionProps) => {
        const { variant = 'default' } = props;

        return {
          root: {
            overflow: 'hidden',
          },
          item: {
            border: 'none',
            borderRadius: '0px',

            ...(variant === 'default' && {
              borderBottom: `1px solid light-dark(${theme.colors.gray[4]}, ${theme.colors['gray-dark'][6]})`,
              '&:first-of-type': {
                borderTopLeftRadius: '0px',
                borderTopRightRadius: '0px',
              },
              '&:last-of-type': {
                borderBottomLeftRadius: '0px',
                borderBottomRightRadius: '0px',
              },
            }),
            ...(variant === 'contained' && {
              borderBottom: `1px solid light-dark(${theme.colors.gray[4]}, ${theme.colors['gray-dark'][6]})`,
              '&:first-of-type': {
                borderTopLeftRadius: '0px',
                borderTopRightRadius: '0px',
              },
              '&:last-of-type': {
                borderBottomLeftRadius: '0px',
                borderBottomRightRadius: '0px',
              },

              // Panel background when expanded (active)
              '&[data-active]': {
                '& .mantine-Accordion-panel': {
                  backgroundColor: `light-dark(${theme.colors.gray[3]}, ${theme.colors['gray-dark'][6]})`,
                },
              },
            }),
            ...(variant === 'filled' && {
              backgroundColor: 'transparent',

              // Panel background when expanded (active)
              '&[data-active]': {
                '& .mantine-Accordion-panel': {
                  border: `1px solid light-dark(${theme.colors.gray[4]}, ${theme.colors['gray-dark'][3]})`,
                  borderTop: 'none',
                  backgroundColor: `light-dark(${theme.colors.gray[2]}, ${theme.colors['gray-dark'][8]})`,
                },
              },
            }),
            ...(variant === 'separated' && {
              // Panel background when expanded (active)
              '&[data-active]': {
                '& .mantine-Accordion-panel': {
                  backgroundColor: 'transparent',
                  border: `1px solid light-dark(${theme.colors.gray[4]}, ${theme.colors['gray-dark'][6]})`,
                  borderTop: 'none',
                },
              },
            }),
          },
          control: {
            padding: `${rem(16)} ${rem(20)}`,
            backgroundColor: 'transparent',
            border: 'none',
            fontSize: theme.fontSizes.md,
            fontWeight: 500,
            color: `light-dark(${theme.colors.gray[9]}, ${theme.colors['gray-dark'][1]})`,

            '&:hover': {
              backgroundColor: `light-dark(${theme.colors.gray[0]}, ${theme.colors['gray-dark'][8]})`,
            },
            '&[data-active]': {
              backgroundColor: `light-dark(${theme.colors.gray[1]}, ${theme.colors['gray-dark'][7]})`,
            },
            ...(variant === 'contained' && {
              '&:hover': {
                backgroundColor: `light-dark(${theme.colors.gray[1]}, ${theme.colors['gray-dark'][7]})`,
              },
              '&[data-active]': {
                backgroundColor: `light-dark(${theme.colors.gray[3]}, ${theme.colors['gray-dark'][6]})`,

                '&:first-of-type': {
                  borderTopLeftRadius: '0px',
                  borderTopRightRadius: '0px',
                },
                '&:last-of-type': {
                  borderBottomLeftRadius: '0px',
                  borderBottomRightRadius: '0px',
                },
              },
            }),
            ...(variant === 'filled' && {
              '&:hover': {
                backgroundColor: 'transparent',
              },
              '&[data-active]': {
                border: `1px solid light-dark(${theme.colors.gray[4]}, ${theme.colors['gray-dark'][3]})`,
                borderBottom: 'none',
                backgroundColor: `light-dark(${theme.colors.gray[2]}, ${theme.colors['gray-dark'][8]})`,
              },
            }),
            ...(variant === 'separated' && {
              borderRadius: '0px',

              '&[data-active]': {
                backgroundColor: 'transparent',
                border: `1px solid light-dark(${theme.colors.gray[4]}, ${theme.colors['gray-dark'][6]})`,
                borderBottom: 'none',
              },
            }),
          },
          chevron: {
            color: `light-dark(${theme.colors.gray[6]}, ${theme.colors['gray-dark'][4]})`,
            width: rem(18),
            height: rem(18),

            '&[data-rotate]': {
              transform: 'rotate(180deg)',
            },
          },
          icon: {
            marginRight: rem(12),
            color: `light-dark(${theme.colors.gray[7]}, ${theme.colors['gray-dark'][3]})`,
          },
          label: {
            lineHeight: 1.4,
          },
          panel: {
            backgroundColor: 'transparent',
          },
          content: {
            color: `light-dark(${theme.colors.gray[7]}, ${theme.colors['gray-dark'][3]})`,
            fontSize: theme.fontSizes.sm,
            lineHeight: 1.6,
          },
        };
      },
    }),

    Badge: Badge.extend({
      defaultProps: {
        variant: 'purple',
        radius: 'sm',
      },
      styles: (theme, props) => {
        return {
          root: {
            fontWeight: 600,

            ...(props.variant === 'filled' &&
              shouldUseNeutralFilledBadge(props.color) && {
                ...UI_TEMPLATE_BADGE_FILLED_NEUTRAL,
              }),

            // Same surfaces as ui-template docs hero + dashboard KPI badges (outline + light + colour prop)
            ...((props.variant === 'outline' || props.variant === 'light') &&
              getUiTemplateSemanticBadgeSurface(props.color)),

            ...(props.variant === 'white' && {
              border: `1px solid light-dark(${theme.colors.gray[4]}, ${theme.colors['gray-dark'][5]})`,
              backgroundColor: `light-dark(${theme.colors.gray[0]}, ${theme.colors['gray-dark'][8]})`,
              color: `light-dark(${theme.colors.gray[7]}, ${theme.colors['gray-dark'][3]})`,
            }),

            ...(props.variant === 'gray' && { ...UI_TEMPLATE_BADGE_SEMANTIC.gray }),

            ...((props.variant === 'green' || props.variant === 'success') && {
              ...UI_TEMPLATE_BADGE_SEMANTIC.success,
            }),

            ...((props.variant === 'orange' || props.variant === 'warning') && {
              ...UI_TEMPLATE_BADGE_SEMANTIC.warning,
            }),

            ...((props.variant === 'red' || props.variant === 'error') && {
              ...UI_TEMPLATE_BADGE_SEMANTIC.error,
            }),

            ...(props.variant === 'blue' && { ...UI_TEMPLATE_BADGE_SEMANTIC.blue }),

            ...((props.variant === 'purple' || props.variant === 'info') && {
              ...UI_TEMPLATE_BADGE_SEMANTIC.brand,
            }),
          },
        };
      },
    }),

    Loader: Loader.extend({
      defaultProps: {
        type: 'oval',
        size: 'md',
        color: 'brand.9',
        loaders: {
          ...defaultLoaders,
          bars: FiveBars, //use our fivebars version to keep a visual compatibility with mantineV6
        },
      },

      vars: (_theme, props) => {
        const map = { xs: 18, sm: 22, md: 36, lg: 44, xl: 58 } as const;

        let sizeVar: string | undefined;
        if (typeof props.size === 'string' && props.size in map) {
          sizeVar = `${map[props.size as keyof typeof map]}px`;
        }

        return {
          root: sizeVar ? { '--loader-size': sizeVar } : {},
        };
      },
      styles: (_theme, props) => {
        if (props.type === 'oval') {
          const spin = keyframes({
            from: { transform: 'rotate(0deg)' },
            to: { transform: 'rotate(360deg)' },
          });
          return {
            root: {
              color: 'var(--loader-color, currentColor)',
              boxSizing: 'border-box',
              width: 'var(--loader-size)',
              height: 'var(--loader-size)',
              borderRadius: '50%',
              borderStyle: 'solid',
              borderWidth: 'calc(var(--loader-size) / 8)',
              borderColor: 'color-mix(in srgb, currentColor 28%, transparent)',
              borderTopColor: 'currentColor',
              animation: `${spin} 1.2s linear infinite`,
              '&::after': { display: 'none !important' },
              '&::before': { display: 'none !important' },
            },
          };
        }

        return {
          root: {},
        };
      },
    }),

    Progress: Progress.extend({
      defaultProps: {
        size: 'xl',
        radius: 'xl',
        animated: false,
        striped: false,
        color: 'brand.9',
      },

      styles: (
        theme: MantineTheme,
        props: {
          size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | number | string;
          radius?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | number | string;
        }
      ) => {
        const bg = `light-dark(${theme.colors.gray?.[2]}, ${theme.colors.gray?.[6]})`;
        const sizeToFont: Record<'xs' | 'sm' | 'md' | 'lg' | 'xl', string> = {
          xs: theme.fontSizes.xs,
          sm: theme.fontSizes.xs,
          md: theme.fontSizes.xs,
          lg: theme.fontSizes.sm,
          xl: theme.fontSizes.sm,
        };
        const effectiveSize = (props.size ?? 'xl') as
          | 'xs'
          | 'sm'
          | 'md'
          | 'lg'
          | 'xl'
          | number
          | string;

        const fontSize =
          typeof effectiveSize === 'string'
            ? (sizeToFont[effectiveSize as 'xs' | 'sm' | 'md' | 'lg' | 'xl'] ?? theme.fontSizes.sm)
            : theme.fontSizes.sm;
        const heightPx =
          typeof effectiveSize === 'number'
            ? `${effectiveSize}px`
            : effectiveSize === 'xl'
              ? '16px'
              : undefined;

        const rpx = getRadius(props.radius ?? 'xl');

        return {
          root: {
            backgroundColor: bg,
            ...(heightPx ? { height: heightPx } : {}),
          },
          section: {
            borderRadius: rpx,
          },
          label: {
            fontSize,
            fontWeight: 500,
            paddingRight: '0.5rem',
            marginLeft: 'auto',
          },
        };
      },
    }),

    Code: Code.extend({
      styles: (theme: MantineTheme) => ({
        root: {
          backgroundColor: `light-dark(${theme.colors.gray[3]}, ${theme.colors.gray[9]}) !important`,
          color: `light-dark(${theme.colors.gray[9]}, ${theme.colors.gray[3]}) !important`,
        },
      }),
    }),

    Select: Select.extend({
      styles: (theme) => {
        return {
          dropdown: {
            padding: `${theme.spacing.xs} 0px !important`,
          },

          option: {
            padding: `${theme.spacing.sm} ${theme.spacing.md}`,
            '&:hover': {
              backgroundColor: `light-dark(${theme.colors?.gray?.[2]}, ${theme.colors?.gray?.[7]})`,
            },
          },
        };
      },
    }),

    Pagination: Pagination.extend({
      styles: (theme) => ({
        control: {
          backgroundColor: `light-dark(${theme.colors?.gray?.[1]}, ${theme.colors?.gray?.[7]})`,
          color: `light-dark(${theme.black}, ${theme.white})`,
          border: `1px solid light-dark(${theme.colors.gray[4]}, ${theme.colors.gray[8]})`,

          '&[data-active]': {
            backgroundColor: theme.colors.brand[9],
            color: theme.white,
          },
        },
      }),
    }),

    Drawer: Drawer.extend({
      styles: () => ({
        header: {
          backgroundColor: 'transparent',
          boxShadow: 'none',
        },
        close: {
          '&:hover': {
            backgroundColor: `light-dark(${theme.colors?.gray?.[3]}, ${theme.colors?.gray?.[9]})`,
          },
        },
      }),
    }),

    Dropzone: Dropzone.extend({
      styles: (theme: MantineTheme) => ({
        root: {
          backgroundColor: `light-dark(${theme.white}, ${theme.colors['gray-dark'][9]}) !important`,
          padding: `${theme.spacing.md} ${theme.spacing.xl}`,
          '--loader-color': theme.colors?.red?.[6], // Or any theme color you want

          '&:hover': {
            backgroundColor: `light-dark(${theme.colors.gray[1]}, ${theme.colors['gray-dark'][8]}) !important`,
          },

          '&[data-loading]': {
            backgroundColor: `light-dark(${theme.white}, ${theme.colors['gray-dark'][9]}) !important`,
            cursor: 'loading',
          },

          '&[data-disabled]': {
            cursor: 'not-allowed',
          },
          '&[data-disabled]:hover': {
            backgroundColor: `light-dark(${theme.white}, ${theme.colors['gray-dark'][9]}) !important`,
          },
        },
      }),
    }),

    LoadingOverlay: LoadingOverlay.extend({
      styles: () => ({
        overlay: {
          background: `light-dark(${theme.colors?.gray?.[1]}, ${theme.colors?.gray?.[1]}), 0.75`,
        },
      }),
    }),
  },
});

/** Keeps native `color-scheme` in step with Mantine on `:root` (replaces color-scheme-alignment.css). */
export const mantineRootColorSchemeGlobalStyles = css`
  :root[data-mantine-color-scheme='light'] {
    color-scheme: light;
  }

  :root[data-mantine-color-scheme='dark'] {
    color-scheme: dark;
  }
`;
